import { CopyToClipboard } from '@movingimage-evp/mi-ui-component-library';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { ECdnType, useGetWebcastSetupSummaryQuery } from '../../../../generated/graphql-manager';
import { formatEventStartDate } from '../../../../utils';
import {
  findAccessCodePolicy,
  findEmailWhitelistPolicy,
  findSingleSignOnPolicy,
} from '../../../../utils/graphql-helpers';
import { Languages } from '../../../components/language';
import { getWebcastTitle, useGetWebcastName } from '../../../hooks/use-get-webcast-name';

import styles from './basic-event-info.module.css';

const COPY = 'manager.webcastSetup.summary.basicInfo';

export function BasicEventInfo() {
  const { webcastId = '' } = useParams();
  const name = useGetWebcastName();
  const { t } = useTranslation();

  const { data } = useGetWebcastSetupSummaryQuery({ variables: { webcastId }, skip: !webcastId });
  const webcast = data?.webcast;
  const primaryLanguage = webcast?.primaryLang?.code;
  const contents = webcast?.contents;
  const webcastTitle = getWebcastTitle(primaryLanguage, contents);

  const isNameAndTitleDifferent = name !== webcastTitle;
  const accessCodePolicy = findAccessCodePolicy(webcast?.securityPolicies);

  const hasAccessCode = accessCodePolicy?.enabled;
  const hasEmailWhitelist = findEmailWhitelistPolicy(webcast?.securityPolicies)?.enabled;
  const hasSso = findSingleSignOnPolicy(webcast?.securityPolicies)?.enabled;

  const getAccessType = () => {
    if (!hasAccessCode && !hasEmailWhitelist && !hasSso) return 'public';
    if (hasSso && hasEmailWhitelist && hasAccessCode) return 'ssoAndEmailWhitelistAndAccessCode';
    if (hasEmailWhitelist && hasAccessCode && !hasSso) return 'emailWhitelistAndAccessCode';
    if (hasSso && hasAccessCode && !hasEmailWhitelist) return 'ssoAndAccessCode';
    if (hasSso && hasEmailWhitelist && !hasAccessCode) return 'ssoAndEmailWhitelist';
    if (hasSso && !hasEmailWhitelist && !hasAccessCode) return 'singleSignOn';
    if (hasEmailWhitelist && !hasSso && !hasAccessCode) return 'emailWhitelist';
    if (hasAccessCode && !hasEmailWhitelist && !hasSso) return 'accessCode';

    return 'public';
  };

  const accessType = getAccessType();

  const multiLanguages = webcast?.additionalLangs && webcast.additionalLangs.length > 0;

  const ecdnAvailable = webcast?.ecdnSettings !== null;
  const ecdnStatus = webcast?.ecdnSettings?.isActive ? 'on' : 'off';
  const ecdnType = webcast?.ecdnSettings?.type || ECdnType.HIVE;

  return (
    <section className={styles.wrapper}>
      <div className={styles.infoWrapper}>
        <span>{t(`${COPY}.nameLabel`)}:</span>
        <span data-testid="event-name">{name}</span>
      </div>

      {isNameAndTitleDifferent && (
        <div className={styles.infoWrapper}>
          <span>{t(`${COPY}.titleLabel`)}:</span>
          <span data-testid="event-title">{webcastTitle}</span>
        </div>
      )}

      <div className={styles.infoWrapper}>
        <span>{t(`${COPY}.startDateLabel`)}:</span>
        <span data-testid="event-start-date">
          {formatEventStartDate(webcast?.plannedStartAt, webcast?.durationInMinutes)}
        </span>
      </div>

      {multiLanguages && (
        <div className={styles.infoWrapper}>
          <span>{t(`${COPY}.languageLabel`)}:</span>
          <div data-testid="event-languages">
            <Languages languages={[webcast?.primaryLang, ...(webcast?.additionalLangs || [])]} />
          </div>
        </div>
      )}

      <div className={styles.infoWrapper}>
        <span>{t(`${COPY}.messagingLabel`)}:</span>
        <span data-testid="event-messaging">
          {t(`${COPY}.messagingOptions.${webcast?.chat?.settings.type || 'none'}`)}
        </span>
      </div>

      <div className={styles.infoWrapper}>
        <span>{t(`${COPY}.accessLabel`)}:</span>

        <span data-testid="event-access">{t(`${COPY}.accessOptions.${accessType}`)}</span>

        {hasAccessCode && (
          <div data-testid="event-access-code" className={styles.accessCode}>
            {accessCodePolicy.accessCode}
            <CopyToClipboard value={accessCodePolicy.accessCode} notificationPosition="right" />
          </div>
        )}
      </div>

      {ecdnAvailable && (
        <div data-testid="ecdn" className={styles.infoWrapper}>
          <span>{t(`${COPY}.ecdnLabel`)}:</span>
          <span data-testid="event-ecdn">{t(`${COPY}.ecdnOptions.${ecdnType}.${ecdnStatus}`)}</span>
        </div>
      )}
    </section>
  );
}
